/**
 * Task #1435 — banner contestuale che avvisa l'admin tenant quando un tier
 * (utenti / agenti AI) sta per esaurirsi (≥80%, status=warning) o è già pieno
 * (status=full). Da inserire in cima alle pagine Utenti e Agenti AI.
 */
import { AlertTriangle, AlertOctagon } from 'lucide-react';
import type { TenantLimitSection } from '@easyflow/shared';
import { useTenantUsage, type TenantUsageTier } from '@/hooks/useTenantUsage';

interface Props {
  tier: TenantUsageTier;
  /** Etichetta singolare/plurale per il messaggio (default per tier). */
  resourceLabel?: string;
}

const DEFAULT_LABELS: Record<TenantUsageTier, string> = {
  voip: 'canali VoIP simultanei',
  users: 'utenti',
  ai_agents: 'agenti AI',
};

export function TenantUsageBanner({ tier, resourceLabel }: Props): JSX.Element | null {
  const { data } = useTenantUsage();
  if (!data) return null;
  const section: TenantLimitSection = data[tier];
  if (section.status === 'ok') return null;

  const label = resourceLabel || DEFAULT_LABELS[tier];
  const isFull = section.status === 'full';
  const Icon = isFull ? AlertOctagon : AlertTriangle;
  const containerClass = isFull
    ? 'border-red-400/50 bg-red-500/10 text-red-900 dark:text-red-100'
    : 'border-amber-400/50 bg-amber-500/10 text-amber-900 dark:text-amber-100';
  const iconClass = isFull ? 'text-red-600 dark:text-red-300' : 'text-amber-600 dark:text-amber-300';

  const title = isFull
    ? `Limite ${label} raggiunto (${section.current}/${section.limit})`
    : `Stai per esaurire i ${label} disponibili (${section.current}/${section.limit})`;
  const body = isFull
    ? `Non puoi creare nuovi ${label} finché il provider non aumenta il piano. Contatta il provider per estendere il limite del tenant.`
    : `Hai usato l'${Math.round((section.current / Math.max(section.limit, 1)) * 100)}% del piano. Contatta il provider per estendere il limite prima di esaurire i ${label}.`;

  return (
    <div
      className={`flex items-start gap-3 rounded-lg border px-4 py-3 ${containerClass}`}
      role={isFull ? 'alert' : 'status'}
      data-testid={`tenant-usage-banner-${tier}`}
    >
      <Icon className={`h-5 w-5 mt-0.5 shrink-0 ${iconClass}`} />
      <div className="flex-1 text-sm">
        <p className="font-semibold">{title}</p>
        <p className="opacity-90">{body}</p>
      </div>
    </div>
  );
}

export default TenantUsageBanner;
